import {
  sanitizeTenantAnalyticsPath,
  sanitizeTenantAnalyticsReferrer,
} from './sanitize';
import type {
  CreateTenantAnalyticsRuntimeOptions,
  TenantAnalyticsAdapter,
  TenantAnalyticsConsentCategory,
  TenantAnalyticsConsentSnapshot,
  TenantAnalyticsDeliveryError,
  TenantAnalyticsDeliveryResult,
  TenantAnalyticsEnvelope,
  TenantAnalyticsEvent,
  TenantAnalyticsItem,
  TenantAnalyticsRuntime,
} from './types';

interface RuntimeAdapter extends TenantAnalyticsAdapter {
  readonly enabled: boolean;
}

const MAX_IDENTIFIER_LENGTH = 256;
const MAX_EVENT_ID_LENGTH = 128;
const MAX_EVENT_TEXT_LENGTH = 512;
const MAX_PURCHASE_ITEMS = 500;
const MAX_POLICY_VERSION_LENGTH = 128;
const ISO_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const ISO_CURRENCY = /^[a-z]{3}$/i;
const TENANT_ANALYTICS_ENVIRONMENTS = new Set([
  'development',
  'preview',
  'production',
]);
const TENANT_MARKETING_PROVIDERS = new Set([
  'google_tag_manager',
  'google_analytics_4',
  'google_ads',
  'meta',
  'tiktok',
  'snapchat',
]);
const CONSENT_CATEGORIES = new Set(['necessary', 'analytics', 'advertising']);

export function createDeniedTenantAnalyticsConsent(
  policyVersion: string,
  updatedAt: string,
): TenantAnalyticsConsentSnapshot {
  return Object.freeze({
    necessary: true,
    analytics: false,
    advertising: false,
    policyVersion,
    updatedAt,
  });
}

/**
 * Creates an immutable, tenant-scoped delivery runtime.
 *
 * The runtime intentionally has no provider registration methods. Construct a
 * new instance when tenant configuration changes; never retain one as a
 * process-global singleton.
 */
export function createTenantAnalyticsRuntime(
  options: CreateTenantAnalyticsRuntimeOptions,
): TenantAnalyticsRuntime {
  if (!isNonEmptyBoundedString(options.tenantId, MAX_IDENTIFIER_LENGTH)) {
    throw new Error('A tenant analytics runtime requires a tenantId.');
  }

  const tenantId = options.tenantId.trim();

  if (!TENANT_ANALYTICS_ENVIRONMENTS.has(options.environment)) {
    throw new Error('Invalid tenant analytics environment.');
  }

  const context = Object.freeze({
    tenantId,
    environment: options.environment,
  });
  const adapters = snapshotAdapters(options.adapters ?? []);
  const enabled = options.enabled === true;

  return Object.freeze({
    context,
    enabled,
    track: async (
      event: TenantAnalyticsEvent,
      consent: TenantAnalyticsConsentSnapshot,
    ) => {
      const normalizedEvent = normalizeEvent(event);
      const consentSnapshot = snapshotConsent(consent);

      if (!normalizedEvent) {
        return adapters.map((adapter) =>
          createSkippedResult(adapter, 'invalid_event'),
        );
      }

      if (!consentSnapshot) {
        return adapters.map((adapter) =>
          createSkippedResult(adapter, 'invalid_consent'),
        );
      }

      const envelope = Object.freeze({
        context,
        consent: consentSnapshot,
        event: normalizedEvent,
      }) satisfies TenantAnalyticsEnvelope;

      const attempts = adapters.map(async (adapter) => {
        if (!enabled) {
          return createSkippedResult(adapter, 'runtime_disabled');
        }

        if (!adapter.enabled) {
          return createSkippedResult(adapter, 'adapter_disabled');
        }

        const missingConsent = adapter.requiredConsent.filter(
          (category) => !hasConsent(consentSnapshot, category),
        );

        if (missingConsent.length > 0) {
          return createSkippedResult(adapter, 'consent_denied', missingConsent);
        }

        await adapter.deliver(envelope);

        return {
          adapterId: adapter.id,
          provider: adapter.provider,
          status: 'delivered' as const,
        };
      });
      const settled = await Promise.allSettled(attempts);

      return settled.map((result, index): TenantAnalyticsDeliveryResult => {
        if (result.status === 'fulfilled') {
          return result.value;
        }

        const adapter = adapters[index];

        if (!adapter) {
          throw new Error('Tenant analytics adapter result is out of bounds.');
        }

        return {
          adapterId: adapter.id,
          provider: adapter.provider,
          status: 'failed',
          error: normalizeError(result.reason),
        };
      });
    },
  });
}

function snapshotAdapters(
  adapters: readonly TenantAnalyticsAdapter[],
): readonly RuntimeAdapter[] {
  const adapterIds = new Set<string>();

  const snapshots = adapters.map((adapter) => {
    if (!isNonEmptyBoundedString(adapter.id, MAX_IDENTIFIER_LENGTH)) {
      throw new Error('A tenant analytics adapter requires an id.');
    }

    const id = adapter.id.trim();

    if (adapterIds.has(id)) {
      throw new Error(`Duplicate tenant analytics adapter id: ${id}`);
    }

    adapterIds.add(id);

    if (!TENANT_MARKETING_PROVIDERS.has(adapter.provider)) {
      throw new Error(`Invalid tenant marketing provider: ${adapter.provider}`);
    }

    if (
      !Array.isArray(adapter.requiredConsent) ||
      typeof adapter.deliver !== 'function'
    ) {
      throw new Error(`Invalid tenant analytics adapter: ${id}`);
    }

    const requiredConsent = [...new Set(adapter.requiredConsent)];

    if (requiredConsent.some((category) => !CONSENT_CATEGORIES.has(category))) {
      throw new Error(`Invalid consent requirement for adapter: ${id}`);
    }

    return Object.freeze({
      id,
      provider: adapter.provider,
      requiredConsent: Object.freeze(requiredConsent),
      enabled: adapter.enabled !== false,
      deliver: adapter.deliver.bind(adapter),
    });
  });

  return Object.freeze(snapshots);
}

function snapshotConsent(
  consent: TenantAnalyticsConsentSnapshot,
): TenantAnalyticsConsentSnapshot | null {
  if (
    !consent ||
    consent.necessary !== true ||
    typeof consent.analytics !== 'boolean' ||
    typeof consent.advertising !== 'boolean' ||
    !isNonEmptyBoundedString(
      consent.policyVersion,
      MAX_POLICY_VERSION_LENGTH,
    ) ||
    !isValidTimestamp(consent.updatedAt)
  ) {
    return null;
  }

  return Object.freeze({
    necessary: true,
    analytics: consent.analytics === true,
    advertising: consent.advertising === true,
    policyVersion: consent.policyVersion.trim(),
    updatedAt: consent.updatedAt,
  });
}

function hasConsent(
  consent: TenantAnalyticsConsentSnapshot,
  category: TenantAnalyticsConsentCategory,
) {
  switch (category) {
    case 'necessary':
      return consent.necessary;
    case 'analytics':
      return consent.analytics;
    case 'advertising':
      return consent.advertising;
  }
}

function createSkippedResult(
  adapter: RuntimeAdapter,
  reason: Extract<
    TenantAnalyticsDeliveryResult,
    { status: 'skipped' }
  >['reason'],
  missingConsent?: readonly TenantAnalyticsConsentCategory[],
): TenantAnalyticsDeliveryResult {
  return {
    adapterId: adapter.id,
    provider: adapter.provider,
    status: 'skipped',
    reason,
    ...(missingConsent
      ? { missingConsent: Object.freeze([...missingConsent]) }
      : {}),
  };
}

function normalizeError(reason: unknown): TenantAnalyticsDeliveryError {
  if (reason instanceof Error) {
    return {
      name: reason.name,
      message: reason.message,
    };
  }

  return {
    name: 'AnalyticsDeliveryError',
    message: typeof reason === 'string' ? reason : 'Unknown delivery error',
  };
}

function normalizeEvent(
  event: TenantAnalyticsEvent,
): TenantAnalyticsEvent | null {
  if (
    !event ||
    !isNonEmptyBoundedString(event.eventId, MAX_EVENT_ID_LENGTH) ||
    !isValidTimestamp(event.occurredAt) ||
    typeof event.browserDocumentLocationSafe !== 'boolean'
  ) {
    return null;
  }

  const eventBase = {
    eventId: event.eventId.trim(),
    occurredAt: event.occurredAt,
    browserDocumentLocationSafe: event.browserDocumentLocationSafe,
  } as const;

  switch (event.name) {
    case 'page_view': {
      const path =
        typeof event.path === 'string'
          ? sanitizeTenantAnalyticsPath(event.path)
          : null;

      if (
        !path ||
        !isOptionalBoundedString(event.title, MAX_EVENT_TEXT_LENGTH) ||
        !isOptionalBoundedString(event.referrer, MAX_EVENT_TEXT_LENGTH * 4)
      ) {
        return null;
      }

      const referrer = event.referrer
        ? sanitizeTenantAnalyticsReferrer(event.referrer)
        : undefined;

      return Object.freeze({
        ...eventBase,
        name: 'page_view',
        path,
        ...(event.title ? { title: event.title.trim() } : {}),
        ...(referrer ? { referrer } : {}),
      });
    }

    // view_content and begin_checkout share one content shape; only the
    // semantic name differs, so each case stamps its own literal name.
    case 'view_content': {
      const content = normalizeContentPayload(event);

      return content
        ? Object.freeze({ ...eventBase, name: 'view_content', ...content })
        : null;
    }

    case 'begin_checkout': {
      const content = normalizeContentPayload(event);

      return content
        ? Object.freeze({ ...eventBase, name: 'begin_checkout', ...content })
        : null;
    }

    case 'purchase': {
      const currency = normalizeCurrency(event.currency);

      if (
        !isNonEmptyBoundedString(event.transactionId, MAX_IDENTIFIER_LENGTH) ||
        !isNonNegativeFiniteNumber(event.value) ||
        !currency ||
        !Array.isArray(event.items) ||
        event.items.length === 0 ||
        event.items.length > MAX_PURCHASE_ITEMS
      ) {
        return null;
      }

      const items: TenantAnalyticsItem[] = [];

      for (const item of event.items) {
        const normalizedItem = normalizeItem(item);

        if (!normalizedItem) {
          return null;
        }

        items.push(normalizedItem);
      }

      return Object.freeze({
        ...eventBase,
        name: 'purchase',
        transactionId: event.transactionId.trim(),
        value: event.value,
        currency,
        items: Object.freeze(items),
      });
    }

    default:
      return null;
  }
}

type NormalizedContentPayload =
  | {
      readonly contentId: string;
      readonly contentType: string;
      readonly contentName?: string;
    }
  | {
      readonly contentId: string;
      readonly contentType: string;
      readonly contentName?: string;
      readonly value: number;
      readonly currency: string;
    };

function normalizeContentPayload(event: {
  readonly contentId: string;
  readonly contentType: string;
  readonly contentName?: string;
  readonly value?: number;
  readonly currency?: string;
}): NormalizedContentPayload | null {
  if (
    !isNonEmptyBoundedString(event.contentId, MAX_IDENTIFIER_LENGTH) ||
    !isNonEmptyBoundedString(event.contentType, MAX_IDENTIFIER_LENGTH) ||
    !isOptionalBoundedString(event.contentName, MAX_EVENT_TEXT_LENGTH)
  ) {
    return null;
  }

  const content = {
    contentId: event.contentId.trim(),
    contentType: event.contentType.trim(),
    ...(event.contentName ? { contentName: event.contentName.trim() } : {}),
  };

  if (event.value === undefined) {
    return event.currency === undefined ? content : null;
  }

  const currency = normalizeCurrency(event.currency);

  if (!isNonNegativeFiniteNumber(event.value) || !currency) {
    return null;
  }

  return { ...content, value: event.value, currency };
}

function normalizeItem(item: TenantAnalyticsItem) {
  if (
    !item ||
    !isNonEmptyBoundedString(item.itemId, MAX_IDENTIFIER_LENGTH) ||
    !isOptionalBoundedString(item.itemName, MAX_EVENT_TEXT_LENGTH) ||
    !isOptionalBoundedString(item.category, MAX_EVENT_TEXT_LENGTH) ||
    !isPositiveFiniteNumber(item.quantity) ||
    !isNonNegativeFiniteNumber(item.price)
  ) {
    return null;
  }

  return Object.freeze({
    itemId: item.itemId.trim(),
    ...(item.itemName ? { itemName: item.itemName.trim() } : {}),
    ...(item.category ? { category: item.category.trim() } : {}),
    quantity: item.quantity,
    price: item.price,
  });
}

function normalizeCurrency(currency: unknown): string | null {
  if (typeof currency !== 'string') {
    return null;
  }

  const normalized = currency.trim();

  return ISO_CURRENCY.test(normalized) ? normalized.toUpperCase() : null;
}

function isNonEmptyBoundedString(
  value: unknown,
  maximumLength: number,
): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= maximumLength &&
    !hasControlCharacter(value)
  );
}

function isOptionalBoundedString(
  value: unknown,
  maximumLength: number,
): value is string | undefined {
  return value === undefined || isNonEmptyBoundedString(value, maximumLength);
}

function isValidTimestamp(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length <= 64 &&
    ISO_TIMESTAMP.test(value) &&
    Number.isFinite(Date.parse(value))
  );
}

function isNonNegativeFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function hasControlCharacter(value: string) {
  for (let index = 0; index < value.length; index += 1) {
    const characterCode = value.charCodeAt(index);

    if (characterCode <= 31 || characterCode === 127) {
      return true;
    }
  }

  return false;
}
